/*
  Facets (collection + search): AJAX filter/sort through the Section Rendering API.
  F1 (2026-09): one form for every breakpoint; [data-facet-input] chips toggle a form
  input; a failed fetch falls back to a full page load with the filters applied; the
  loading state is always cleared; the ≤1024px sheet is focus-trapped with Esc / backdrop
  close and focus return; the sticky toolbar gets .is-stuck from a sentinel observer.
*/
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper && typeof onKeyUpEscape === 'function') facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static pageUrl(searchParams) {
    return `${window.location.pathname}${searchParams ? '?'.concat(searchParams) : ''}`;
  }

  static setLoading(isLoading) {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const grid = gridContainer.querySelector('.collection');
      if (grid) grid.classList.toggle('loading', isLoading);
      if (isLoading) gridContainer.setAttribute('aria-busy', 'true');
      else gridContainer.removeAttribute('aria-busy');
    }
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.classList.toggle('loading', isLoading);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) {
      window.location.assign(FacetFiltersForm.pageUrl(searchParams));
      return;
    }
    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.latestUrl = url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, searchParams);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, searchParams) {
    let navigating = false;
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          // 429 / 5xx: let the server render the filtered page instead of a grid stuck in .loading
          navigating = true;
          window.location.assign(FacetFiltersForm.pageUrl(searchParams));
          throw new Error(`facets: section render ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        if (FacetFiltersForm.latestUrl !== url) return; // a newer request superseded this one
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderAll(html, event);
      })
      .catch((error) => {
        if (navigating) return;
        if (error instanceof TypeError) {
          // network failure: same fallback
          window.location.assign(FacetFiltersForm.pageUrl(searchParams));
          return;
        }
        console.error(error);
      })
      .finally(() => {
        if (!navigating) FacetFiltersForm.setLoading(false);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    try {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      FacetFiltersForm.renderAll(html, event);
    } catch (error) {
      console.error(error);
    } finally {
      FacetFiltersForm.setLoading(false);
    }
  }

  static renderAll(html, event) {
    try {
      FacetFiltersForm.renderFilters(html, event);
    } catch (error) {
      console.error(error);
    }
    try {
      FacetFiltersForm.renderProductGridContainer(html);
    } catch (error) {
      console.error(error);
    }
    try {
      FacetFiltersForm.renderProductCount(html);
    } catch (error) {
      console.error(error);
    }
  }

  static renderProductGridContainer(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
    document.dispatchEvent(new CustomEvent('bt:facets:rendered', { bubbles: true }));
  }

  static renderProductCount(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    if (!source) return;
    const count = source.innerHTML;
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.innerHTML = count;
      el.classList.remove('loading');
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event && event.target && event.target.closest ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`#FacetFiltersForm .js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    FacetFiltersForm.decorateSummaries(document.getElementById('FacetFiltersForm'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!activeFacetsElement || !target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
      target.classList.toggle('is-empty', activeFacetsElement.classList.contains('is-empty'));
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    // innerHTML swaps (no listeners on these)
    ['.bt-facets-hidden', '.bt-sort-current', '.bt-sheet__show-count', '.sorting', '.mobile-facets__count'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });
    // outerHTML swaps (attributes matter: hidden, aria-pressed, is-disabled; custom elements re-upgrade)
    ['.bt-facets-trigger__count', '.bt-sheet__clear-wrap'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.outerHTML = source.outerHTML;
    });
    // UK-stock / type chips rendered by the section outside the form
    html.querySelectorAll('[data-facet-input]').forEach((source) => {
      const name = source.getAttribute('data-facet-input');
      const value = source.getAttribute('data-facet-value');
      const target = Array.from(document.querySelectorAll('[data-facet-input]')).find(
        (el) => el.getAttribute('data-facet-input') === name && el.getAttribute('data-facet-value') === value
      );
      if (target) target.outerHTML = source.outerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      targetElementAccessibility.outerHTML = sourceElementAccessibility.outerHTML;
    }
  }

  // global.js decorates [id^="Details-"] summaries once at load; re-apply after a swap.
  static decorateSummaries(root) {
    if (!root) return;
    root.querySelectorAll('details > summary').forEach((summary) => {
      const details = summary.parentNode;
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', details.hasAttribute('open'));
      if (summary.nextElementSibling && summary.nextElementSibling.id) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      }
      if (summary.dataset.btDecorated) return;
      summary.dataset.btDecorated = '1';
      summary.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });
    });
  }

  static syncSort(value) {
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (select.value !== value) select.value = value;
    });
    let currentName = null;
    document.querySelectorAll('input[type="radio"][name="sort_by"]').forEach((radio) => {
      radio.checked = radio.value === value;
      if (radio.checked) {
        const label = radio.closest('label');
        const name = label ? label.querySelector('.name') : null;
        if (name) currentName = name.textContent.trim();
      }
    });
    if (currentName === null) {
      const select = document.querySelector('select[name="sort_by"]');
      if (select && select.selectedIndex >= 0) currentName = select.options[select.selectedIndex].text.trim();
    }
    const current = document.querySelector('.bt-sort-current');
    if (current && currentName !== null) current.textContent = currentName;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', FacetFiltersForm.pageUrl(searchParams));
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.id) return [];
    return [{ section: grid.dataset.id }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    const target = event ? event.target : null;
    const params = new URLSearchParams();
    let sortValue = null;
    if (target && target.name === 'sort_by' && target.value) sortValue = target.value;

    document.querySelectorAll('.no-js-list').forEach((el) => el.remove());
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      new FormData(form).forEach((value, key) => {
        if (key === 'sort_by') {
          if (sortValue === null && value) sortValue = value;
          return;
        }
        params.append(key, value);
      });
    });
    if (sortValue) {
      params.append('sort_by', sortValue);
      FacetFiltersForm.syncSort(sortValue);
    }
    this.onSubmitForm(params.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget.href;
    const url = href.indexOf('?') == -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.latestUrl = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* Chips outside the form ([data-facet-input] + [data-facet-value]) toggle the matching input. */
document.addEventListener('click', (event) => {
  const chip = event.target.closest('[data-facet-input]');
  if (!chip || chip.tagName === 'A') return;
  event.preventDefault();
  const name = chip.getAttribute('data-facet-input');
  const value = chip.getAttribute('data-facet-value');
  const form = document.getElementById('FacetFiltersForm');
  const input = form
    ? Array.from(form.querySelectorAll('input[type="checkbox"]')).find((el) => el.name === name && el.value === value)
    : null;

  if (input) {
    input.checked = !input.checked;
    chip.setAttribute('aria-pressed', String(input.checked));
    chip.classList.toggle('is-active', input.checked);
    const host = form.closest('facet-filters-form');
    if (host) host.onSubmitHandler({ preventDefault() {}, target: input });
    return;
  }

  // No input on this page: toggle the parameter on the current URL instead.
  const params = new URLSearchParams(window.location.search);
  const existing = params.getAll(name);
  params.delete(name);
  let pressed;
  if (existing.includes(value)) {
    existing.filter((v) => v !== value).forEach((v) => params.append(name, v));
    pressed = false;
  } else {
    existing.forEach((v) => params.append(name, v));
    params.append(name, value);
    pressed = true;
  }
  params.delete('page');
  chip.setAttribute('aria-pressed', String(pressed));
  chip.classList.toggle('is-active', pressed);
  FacetFiltersForm.renderPage(params.toString(), null);
});

/* Panel "Apply": closes the panel (AJAX apply already happened on input); price panels force a submit. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('.js-facet-apply');
  if (!button) return;
  const details = button.closest('details');
  if (button.hasAttribute('data-apply-price') && details) {
    const priceInput = details.querySelector('price-range input');
    if (priceInput) priceInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
  if (details) {
    details.removeAttribute('open');
    const summary = details.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    }
  }
});

/* Keep every sort control in step, whichever one changed. */
document.addEventListener('change', (event) => {
  const control = event.target;
  if (!control || control.name !== 'sort_by') return;
  FacetFiltersForm.syncSort(control.value);
});

/* ≤1024px filter/sort sheet: the same #FacetsWrapperDesktop element, opened as a dialog. */
const BtFacetSheet = {
  trigger: null,
  sheet() {
    return document.getElementById('FacetsWrapperDesktop');
  },
  isOpen() {
    const sheet = this.sheet();
    return !!sheet && sheet.classList.contains('is-open');
  },
  backdrop() {
    let backdrop = document.querySelector('.bt-sheet__backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'bt-sheet__backdrop';
      backdrop.hidden = true;
      backdrop.addEventListener('click', () => BtFacetSheet.close());
      document.body.appendChild(backdrop);
    }
    return backdrop;
  },
  open(trigger, target) {
    const sheet = this.sheet();
    if (!sheet || this.isOpen()) return;
    this.trigger = trigger || null;
    sheet.classList.add('is-open');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    document.body.classList.add('bt-sheet-open');
    this.backdrop().hidden = false;
    document.querySelectorAll('.js-bt-sheet-open').forEach((button) => button.setAttribute('aria-expanded', 'true'));

    let focusTarget = sheet.querySelector('.bt-sheet__close');
    const sortDetails = sheet.querySelector('.bt-sheet__sort');
    if (target === 'sort' && sortDetails) {
      sortDetails.setAttribute('open', '');
      const summary = sortDetails.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');
      focusTarget = sortDetails.querySelector('input:checked') || summary || focusTarget;
    }
    if (typeof trapFocus === 'function') {
      trapFocus(sheet, focusTarget);
    } else if (focusTarget) {
      focusTarget.focus();
    }
    document.addEventListener('keydown', this.onKeydown);
  },
  close(restoreFocus = true) {
    const sheet = this.sheet();
    if (!sheet || !this.isOpen()) return;
    sheet.classList.remove('is-open');
    sheet.removeAttribute('role');
    sheet.removeAttribute('aria-modal');
    document.body.classList.remove('bt-sheet-open');
    this.backdrop().hidden = true;
    document.querySelectorAll('.js-bt-sheet-open').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.onKeydown);
    const focusTarget = restoreFocus ? this.trigger : null;
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusTarget);
    } else if (focusTarget) {
      focusTarget.focus();
    }
    this.trigger = null;
  },
  onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    event.preventDefault();
    event.stopPropagation();
    BtFacetSheet.close();
  },
};

document.addEventListener('click', (event) => {
  const opener = event.target.closest('.js-bt-sheet-open');
  if (opener) {
    event.preventDefault();
    BtFacetSheet.open(opener, opener.getAttribute('data-sheet-target'));
    return;
  }
  const closer = event.target.closest('.js-bt-sheet-close');
  if (closer && BtFacetSheet.isOpen()) {
    event.preventDefault();
    BtFacetSheet.close();
  }
});

const btDesktopQuery = window.matchMedia('(min-width: 1025px)');
const onBtDesktopChange = (query) => {
  if (query.matches && BtFacetSheet.isOpen()) BtFacetSheet.close(false);
};
if (typeof btDesktopQuery.addEventListener === 'function') btDesktopQuery.addEventListener('change', onBtDesktopChange);
else if (typeof btDesktopQuery.addListener === 'function') btDesktopQuery.addListener(onBtDesktopChange);

/* Sticky toolbar: a 1px sentinel before .bt-toolbar; .is-stuck once it scrolls under the header. */
(function initStickyToolbar() {
  const toolbar = document.querySelector('.bt-toolbar');
  if (!toolbar || !('IntersectionObserver' in window)) return;
  let sentinel = toolbar.previousElementSibling;
  if (!sentinel || !sentinel.classList.contains('bt-toolbar__sentinel')) {
    sentinel = document.createElement('div');
    sentinel.className = 'bt-toolbar__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    toolbar.parentNode.insertBefore(sentinel, toolbar);
  }
  const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--bt-header-h'), 10) || 56;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        toolbar.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top < headerH + 1);
      });
    },
    { rootMargin: `-${headerH + 1}px 0px 0px 0px`, threshold: 0 }
  );
  observer.observe(sentinel);
})();

FacetFiltersForm.decorateSummaries(document.getElementById('FacetFiltersForm'));

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const link = event.currentTarget;
    if (link && link.getAttribute('aria-disabled') === 'true') return;
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (!form) {
      window.location.assign(link.href);
      return;
    }
    if (BtFacetSheet.isOpen() && link.classList.contains('bt-sheet__clear')) {
      // keep the sheet open; the counts refresh in place
    }
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
